import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  SupervisorAccount as SuperAdminIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import BranchSelector from './BranchSelector';
import axios from 'axios';

const UserManagement = () => {
  const { user, isSuperAdmin, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Agent',
    branchId: '',
    status: 'Active',
    permissions: []
  });

  const roleOptions = isSuperAdmin() 
    ? ['SuperAdmin', 'BranchAdmin', 'Agent', 'Viewer']
    : ['Agent', 'Viewer'];

  const permissionOptions = [
    { resource: 'submissions', actions: ['read', 'write', 'delete'] },
    { resource: 'events', actions: ['read', 'write', 'delete'] },
    { resource: 'users', actions: ['read', 'write', 'delete'] },
    { resource: 'settings', actions: ['read', 'write'] }
  ];

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin()) {
      fetchBranches();
    }
  }, [selectedBranchFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedBranchFilter) {
        params.append('branchId', selectedBranchFilter);
      }
      
      const response = await axios.get(`/api/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/branches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'Agent',
      branchId: isSuperAdmin() ? '' : user?.branchId,
      status: 'Active',
      permissions: [
        { resource: 'submissions', actions: ['read'] },
        { resource: 'events', actions: ['read'] }
      ]
    });
    setOpenDialog(true);
  };

  const handleEditUser = (editUser) => {
    setEditingUser(editUser);
    setFormData({
      name: editUser.name || '',
      email: editUser.email || '',
      password: '',
      role: editUser.role || 'Agent',
      branchId: editUser.branchId?._id || editUser.branchId || '',
      status: editUser.status || 'Active',
      permissions: editUser.permissions || []
    });
    setOpenDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = { ...formData };
      
      // Remove empty password for updates
      if (!userData.password && editingUser) {
        delete userData.password;
      }

      if (editingUser) {
        await axios.put(`/api/users/${editingUser._id}`, userData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('User updated successfully');
      } else {
        await axios.post('/api/users', userData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('User created successfully');
      }
      
      fetchUsers();
      setOpenDialog(false);
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || 'Failed to save user',
        'error'
      );
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('User deleted successfully');
        fetchUsers();
      } catch (error) {
        showSnackbar('Failed to delete user', 'error');
      }
    }
  };

  const handlePermissionChange = (resource, action, checked) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      const existingPermIndex = permissions.findIndex(p => p.resource === resource);
      
      if (existingPermIndex >= 0) {
        if (checked) {
          // Add action if not already present
          if (!permissions[existingPermIndex].actions.includes(action)) {
            permissions[existingPermIndex].actions.push(action);
          }
        } else {
          // Remove action
          permissions[existingPermIndex].actions = permissions[existingPermIndex].actions.filter(a => a !== action);
          // Remove permission if no actions left
          if (permissions[existingPermIndex].actions.length === 0) {
            permissions.splice(existingPermIndex, 1);
          }
        }
      } else if (checked) {
        // Add new permission
        permissions.push({ resource, actions: [action] });
      }
      
      return { ...prev, permissions };
    });
  };

  const hasUserPermission = (resource, action) => {
    return formData.permissions.some(p => 
      p.resource === resource && p.actions.includes(action)
    );
  };

  if (!hasPermission('users', 'read') && !isSuperAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You don't have permission to view users.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users and their permissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isSuperAdmin() && (
            <BranchSelector
              selectedBranchId={selectedBranchFilter}
              onBranchChange={setSelectedBranchFilter}
              label="Filter by Branch"
            />
          )}
          {hasPermission('users', 'write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              sx={{ borderRadius: 2 }}
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      {/* Users Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((tableUser) => (
                  <TableRow key={tableUser._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {tableUser.role === 'SuperAdmin' ? (
                          <SuperAdminIcon color="warning" />
                        ) : (
                          <PersonIcon color="primary" />
                        )}
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {tableUser.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tableUser.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tableUser.role}
                        color={tableUser.role === 'SuperAdmin' ? 'warning' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {tableUser.branchId ? (
                        <Typography variant="body2">
                          {tableUser.branchId.displayName || tableUser.branchId.name}
                        </Typography>
                      ) : (
                        <Chip label="No Branch" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tableUser.status}
                        color={tableUser.status === 'Active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {tableUser.lastLogin 
                        ? new Date(tableUser.lastLogin).toLocaleDateString()
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell align="center">
                      {hasPermission('users', 'write') && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(tableUser)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          {tableUser.role !== 'SuperAdmin' && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(tableUser._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Form Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                fullWidth
                required={!editingUser}
                helperText={editingUser ? "Leave blank to keep current password" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  label="Role"
                >
                  {roleOptions.map(role => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {isSuperAdmin() && formData.role !== 'SuperAdmin' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Branch</InputLabel>
                  <Select
                    value={formData.branchId}
                    onChange={(e) => setFormData(prev => ({ ...prev, branchId: e.target.value }))}
                    label="Branch"
                    required
                  >
                    {branches.map(branch => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.role !== 'SuperAdmin' && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                {permissionOptions.map(({ resource, actions }) => (
                  <Box key={resource} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                      {resource}
                    </Typography>
                    <FormGroup row>
                      {actions.map(action => (
                        <FormControlLabel
                          key={`${resource}-${action}`}
                          control={
                            <Checkbox
                              checked={hasUserPermission(resource, action)}
                              onChange={(e) => handlePermissionChange(resource, action, e.target.checked)}
                            />
                          }
                          label={action}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                ))}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            disabled={!formData.name || !formData.email || (!formData.password && !editingUser)}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
};

export default UserManagement;